<template>
  <div class="reservas-beneficiary">
    <h2 class="subheading grey--text">Reservas Recibidas</h2>

    <v-container class="my-5">

      <!-- Alerta error -->
      <v-alert v-if="error" type="warning" outline transition="scale-transition" class="mb-3">
        {{ error }}
      </v-alert>

      <!-- Tabs con contenido arriba y pestañas abajo -->
      <v-tabs v-model="activeTab" color="white" slider-color="primary">

        <!-- TAB 1: RESERVAS -->
        <v-tab-item value="reservas">
          <v-card class="elevation-5">
            <v-toolbar dark color="primary">
              <v-icon class="mr-2">event</v-icon>
              <v-toolbar-title>Reservas Recibidas</v-toolbar-title>
            </v-toolbar>

            <v-card-text>
              <v-layout row wrap>
                <v-flex
                  v-for="reservation in beneficiaryReservations"
                  :key="reservation.id"
                  xs12 sm6 md4
                  class="pa-2"
                >
                  <v-card
                    class="elevation-2"
                    :class="selectedReservation && selectedReservation.id === reservation.id ? 'primary lighten-5' : ''"
                    @click="selectReservation(reservation)"
                    style="cursor: pointer"
                  >
                    <v-card-title class="pa-3">
                      <v-icon left color="primary">event</v-icon>
                      <span class="body-2 font-weight-bold">{{ reservation.owner_company_name }}</span>
                      <v-spacer></v-spacer>
                      <v-chip :color="getStatusColor(reservation.status)" text-color="white" small>{{ formatStatus(reservation.status) }}</v-chip>
                    </v-card-title>
                    <v-divider></v-divider>
                    <div class="reservation-info px-3 py-2">
                      <div class="info-row">
                        <v-icon small color="grey darken-1" class="info-icon">date_range</v-icon>
                        <div class="info-content">
                          <div class="info-label">Período</div>
                          <div class="info-value">{{ formatDate(reservation.start_date) }} — {{ formatDate(reservation.end_date) }}</div>
                        </div>
                      </div>

                      <div class="info-row">
                        <v-icon small color="grey darken-1" class="info-icon">local_parking</v-icon>
                        <div class="info-content">
                          <div class="info-label">Cajones asignados</div>
                          <div class="info-value">{{ reservation.reserved_spaces.length }}</div>
                        </div>
                      </div>

                      <div class="info-row">
                        <v-icon small color="grey darken-1" class="info-icon">{{ reservation.is_private_vehicles ? 'directions_car' : 'local_shipping' }}</v-icon>
                        <div class="info-content">
                          <div class="info-label">Tipo</div>
                          <v-chip :color="reservation.is_private_vehicles ? 'info' : 'warning'" text-color="white" x-small class="ma-0 info-chip">
                            {{ reservation.is_private_vehicles ? 'Privados' : 'Transportistas' }}
                          </v-chip>
                        </div>
                      </div>

                      <div class="info-row">
                        <v-icon small color="grey darken-1" class="info-icon">qr_code</v-icon>
                        <div class="info-content">
                          <div class="info-label">Códigos creados</div>
                          <div class="info-value">{{ getCodesCountByReservation(reservation.id) }}</div>
                        </div>
                      </div>
                    </div>
                    <v-card-actions>
                    <v-btn flat small color="primary" :disabled="reservation.status === 'canceled'" @click.stop="openCreateCodeFromReservation(reservation)">
                        <v-icon left small>add</v-icon>
                        Nuevo código
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-chip
                        :color="selectedReservation && selectedReservation.id === reservation.id ? 'primary' : 'grey lighten-1'"
                        text-color="white"
                        small
                      >
                        {{ selectedReservation && selectedReservation.id === reservation.id ? 'Seleccionada' : 'Seleccionar' }}
                      </v-chip>
                    </v-card-actions>
                  </v-card>
                </v-flex>

                <v-flex v-if="beneficiaryReservations.length === 0" xs12>
                  <p class="text-xs-center grey--text subheading py-4">No tienes reservas disponibles</p>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- TAB 2: CÓDIGOS QR -->
        <v-tab-item value="codigos">
          <v-card class="elevation-5">
            <v-toolbar dark color="primary" class="mb-2">
              <v-icon class="mr-2">qr_code</v-icon>
              <v-toolbar-title>Códigos QR</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    <v-btn dark flat :disabled="!selectedReservation || selectedReservation.status === 'canceled'" @click="openCreateCodeDialog">
                      <v-icon left>add</v-icon>
                      Nuevo Código
                    </v-btn>
                  </span>
                </template>
                <span v-if="!selectedReservation">Selecciona una reserva en la pestaña "Mis Reservas" primero</span>
                <span v-else-if="selectedReservation.status === 'canceled'">No puedes crear códigos para una reserva cancelada</span>
              </v-tooltip>
            </v-toolbar>

            <v-card-text>
              <!-- Filtros -->
              <v-layout row wrap align-center class="mb-3">
                <v-flex xs12 sm6 md4 class="pr-2">
                  <v-select
                    v-model="filterCodeStatus"
                    :items="codeStatusOptions"
                    label="Estado"
                    prepend-icon="filter_list"
                    clearable
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 md4 class="pr-2">
                  <v-text-field
                    v-model="searchCodeText"
                    label="Buscar visitante"
                    prepend-icon="search"
                    clearable
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-select
                    v-model="filterCodeReservation"
                    :items="beneficiaryReservations"
                    item-text="owner_company_name"
                    item-value="id"
                    label="Filtrar por reserva"
                    prepend-icon="event"
                    clearable
                    hide-details
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-card-text>

            <!-- Tabla -->
            <v-data-table
              :headers="headersCode"
              :items="filteredCodes"
              :loading="loadingCodes"
              :pagination.sync="paginationCodes"
              :rows-per-page-text="'Filas por página'"
              :rows-per-page-items="[10, 25, 50, { text: 'Todos', value: -1 }]"
              :no-data-text="'No hay códigos registrados'"
              class="elevation-1"
            >
              <template v-slot:items="props">
                <td>
                  <span class="font-weight-medium">{{ props.item.provider_name }}</span>
                  <br>
                  <span class="caption grey--text">{{ props.item.contact_email || props.item.contact_phone }}</span>
                </td>
                <td>{{ props.item.number || props.item.space_number }}</td>
                <td>
                  <span>{{ formatDate(props.item.invitation_date) }}</span>
                  <span class="grey--text"> — {{ formatDate(props.item.expiration) }}</span>
                  <br v-if="props.item.start_time || props.item.end_time">
                  <span v-if="props.item.start_time || props.item.end_time" class="caption">
                    {{ props.item.start_time || '00:00' }} — {{ props.item.end_time || '23:59' }}
                  </span>
                </td>
                <td>
                  <v-chip 
                    :color="getExpirationColor(props.item.expiration_status)" 
                    text-color="white" 
                    small
                  >
                    <v-icon left x-small>{{ getExpirationIcon(props.item.expiration_status) }}</v-icon>
                    {{ formatExpirationMessage(props.item) }}
                  </v-chip>
                </td>
                <td>
                  <v-chip :color="getQRStatusColor(props.item.qr_status)" text-color="white" small>{{ formatQRStatus(props.item.qr_status) }}</v-chip>
                </td>
                <td class="justify-center layout px-0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" v-on="on" @click="viewCodeDetails(props.item)">visibility</v-icon>
                    </template>
                    <span>Ver detalles</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="props.item.qr_status === 'pending'">
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" color="green" v-on="on" @click="toggleCodeStatus(props.item, 'activate')">check_circle</v-icon>
                    </template>
                    <span>Activar</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="props.item.qr_status === 'active'">
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" color="orange" v-on="on" @click="toggleCodeStatus(props.item, 'deactivate')">pause_circle_filled</v-icon>
                    </template>
                    <span>Desactivar</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="props.item.qr_status === 'inactive'">
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" color="green" v-on="on" @click="toggleCodeStatus(props.item, 'activate')">play_circle_filled</v-icon>
                    </template>
                    <span>Reactivar</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="['active', 'inactive', 'pending'].includes(props.item.qr_status)">
                    <template v-slot:activator="{ on }">
                      <v-icon medium class="mr-2" color="red" v-on="on" @click="openDeleteConfirmDialog(props.item)">delete</v-icon>
                    </template>
                    <span>Cancelar código</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon medium v-on="on" @click="openResendDialog(props.item)">send</v-icon>
                    </template>
                    <span>Reenviar código</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon medium color="red" v-on="on" @click="downloadPDF(props.item)">picture_as_pdf</v-icon>
                    </template>
                    <span>Descargar PDF</span>
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab href="#reservas">
          <v-icon left>event</v-icon>
          Mis Reservas
        </v-tab>
        <v-tab href="#codigos">
          <v-icon left>qr_code</v-icon>
          Mis Códigos QR
        </v-tab>

      </v-tabs>

    <!-- Dialog: Crear Código QR -->
    <QRCodeDialog
      v-if="selectedReservation"
      :dialog="dialogCreateCode"
      :reservation="selectedReservation"
      @created="handleCodeCreated"
      @close="dialogCreateCode = false"
    />

    <!-- Dialog: Detalles Código -->
    <v-dialog v-model="dialogCodeDetails" max-width="650px">
      <v-card v-if="selectedCode">
        <v-toolbar dark color="primary">
          <v-icon class="mr-2">qr_code</v-icon>
          <v-toolbar-title>Detalles del Código QR</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="red" @click="dialogCodeDetails = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-chip :color="getQRStatusColor(selectedCode.qr_status)" text-color="white" label class="mb-4">{{ formatQRStatus(selectedCode.qr_status) }}</v-chip>
          
          <v-layout row wrap>
            <!-- Código QR -->
            <v-flex xs12 class="mb-3">
              <v-text-field 
                :value="selectedCode.qr_code || '-'" 
                label="Código QR" 
                prepend-icon="qr_code" 
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Visitante y Empresa -->
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field 
                :value="selectedCode.provider_name || selectedCode.first_name + ' ' + selectedCode.last_name || '-'" 
                label="Visitante" 
                prepend-icon="person" 
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2">
              <v-text-field 
                :value="selectedCode.company_name || selectedCode.beneficiary_company_name || '-'" 
                label="Empresa" 
                prepend-icon="business" 
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Cajón -->
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field 
                :value="selectedCode.number || selectedCode.space_number || '-'" 
                label="Cajón" 
                prepend-icon="local_parking" 
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Contacto -->
            <v-flex xs12 sm6 class="pl-2">
              <v-text-field 
                :value="selectedCode.contact_email || selectedCode.email || '-'" 
                label="Email" 
                prepend-icon="email" 
                readonly
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 class="pr-2">
              <v-text-field 
                :value="selectedCode.contact_phone || selectedCode.phone || selectedCode.telephone || '-'" 
                label="Teléfono" 
                prepend-icon="phone" 
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Vigencia -->
            <v-flex xs12 sm6 class="pl-2">
              <v-text-field 
                :value="selectedCode.invitation_date ? formatDate(selectedCode.invitation_date) : '-'" 
                label="Vigencia Inicio" 
                prepend-icon="event" 
                readonly
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                :value="selectedCode.expiration ? formatDate(selectedCode.expiration) : '-'"
                label="Vigencia Fin"
                prepend-icon="event"
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Horarios (si existen) -->
            <v-flex xs12 sm6 class="pr-2" v-if="selectedCode.start_time || selectedCode.end_time">
              <v-text-field
                :value="selectedCode.start_time || '00:00'"
                label="Hora Inicio"
                prepend-icon="access_time"
                readonly
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6 class="pl-2" v-if="selectedCode.start_time || selectedCode.end_time">
              <v-text-field
                :value="selectedCode.end_time || '23:59'"
                label="Hora Fin"
                prepend-icon="access_time"
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Información de Activación -->
            <v-flex xs12 sm6 class="pl-2">
              <v-text-field 
                :value="selectedCode.activated_at ? formatDateTime(selectedCode.activated_at) : '-'" 
                label="Activado en" 
                prepend-icon="check_circle" 
                readonly
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 class="pr-2">
              <v-text-field 
                :value="selectedCode.deactivated_at ? formatDateTime(selectedCode.deactivated_at) : '-'" 
                label="Desactivado en" 
                prepend-icon="cancel" 
                readonly
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm6 class="pl-2">
              <v-text-field 
                :value="selectedCode.used_at ? formatDateTime(selectedCode.used_at) : '-'" 
                label="Usado en" 
                prepend-icon="done_all" 
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Registro de Llegada -->
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field 
                :value="selectedCode.arrival_registered ? 'Sí' : 'No'" 
                label="Llegada registrada" 
                prepend-icon="how_to_reg" 
                readonly
              ></v-text-field>
            </v-flex>

            <!-- Descripción / Notas -->
            <v-flex xs12 v-if="selectedCode.subject || selectedCode.notes">
              <v-text-field 
                :value="selectedCode.subject || selectedCode.notes || '-'" 
                label="Descripción / Notas" 
                prepend-icon="note" 
                readonly
                multi-line
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogCodeDetails = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Reenviar Código -->
    <v-dialog v-model="dialogResend" max-width="450px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-icon class="mr-2">send</v-icon>
          <v-toolbar-title>Reenviar Código QR</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="dialogResend = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-form ref="formResend">
            <v-text-field
              v-model="resendData.email"
              label="Email (opcional)"
              type="email"
              prepend-icon="email"
            ></v-text-field>
            <v-text-field
              v-model="resendData.phone"
              label="Teléfono (opcional)"
              prepend-icon="phone"
            ></v-text-field>
            <p class="caption grey--text">Debes proporcionar al menos email o teléfono</p>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn color="primary" @click="dialogResend = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="loadingResend" @click="handleResendCode">Reenviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Confirmar Cancelación de Código -->
    <v-dialog v-model="dialogConfirmDelete" max-width="450px">
      <v-card>
        <v-toolbar dark color="red">
          <v-icon class="mr-2">warning</v-icon>
          <v-toolbar-title>Cancelar Código QR</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="red" @click="dialogConfirmDelete = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <p class="subheading mb-2">¿Estás seguro que deseas cancelar este código QR?</p>
          <p class="body-1 mb-3" v-if="codeToDelete">
            <strong>Visitante:</strong> {{ codeToDelete.provider_name || codeToDelete.first_name + ' ' + codeToDelete.last_name }}
          </p>
          <p class="caption grey--text mb-4">
            Al cancelar este código:
            <br>✓ Se marcará como cancelado y expirado
            <br>✓ El visitante será notificado por SMS y Email
            <br>✓ No se podrá usar para futuras entradas
            <br>✓ Podrás crear un nuevo código para la misma reserva
          </p>
          
          <v-form ref="formDelete">
            <v-text-field
              v-model="deleteData.reason"
              label="Razón de cancelación (opcional)"
              prepend-icon="note"
              hint="Se incluirá en la notificación al visitante"
              persistent-hint
            ></v-text-field>
            
            <v-checkbox
              v-model="deleteData.notify_provider"
              label="Notificar al visitante"
              color="primary"
            ></v-checkbox>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" @click="dialogConfirmDelete = false">No, mantener</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="red" :loading="loading" @click="handleDeleteCode">Sí, Cancelar código</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    </v-container>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import QRCodeDialog from '@/components/parking/QRCodeDialog.vue'
import moment from 'moment'
import axios from 'axios'
import QRCode from 'qrcode'
import jsPDF from 'jspdf'

export default {
  name: 'ReservationsBeneficiary',
  components: {
    QRCodeDialog
  },
  data() {
    return {
      loading: false,
      loadingCodes: false,
      loadingResend: false,
      error: null,

      activeTab: 'reservas',
      
      dialogCreateCode: false,
      dialogCodeDetails: false,
      dialogResendCode: false,
      dialogResend: false,
      dialogConfirmDelete: false,

      selectedReservation: null,
      selectedCode: null,
      codeToDelete: null,

      filterCodeStatus: null,
      filterCodeReservation: null,
      searchCodeText: '',

      resendData: {
        email: '',
        phone: ''
      },

      deleteData: {
        reason: '',
        notify_provider: true
      },

      codesCountByReservation: {},

      paginationCodes: {
        sortBy: 'invitation_date',
        descending: true,
        page: 1,
        rowsPerPage: 10
      },

      headersCode: [
        { text: 'Visitante', value: 'provider_name', width: '18%' },
        { text: 'Cajón', value: 'space_number', width: '12%' },
        { text: 'Vigencia', value: 'validity', width: '18%' },
        { text: 'Vence en', value: 'expiration_status', width: '12%' },
        { text: 'Estado', value: 'qr_status', width: '12%' },
        { text: 'Acciones', value: 'actions', width: '12%', sortable: false }
      ],

      codeStatusOptions: [
        { text: 'Pendiente', value: 'pending' },
        { text: 'Activo', value: 'active' },
        { text: 'Inactivo', value: 'inactive' },
        { text: 'En uso', value: 'in_use' },
        { text: 'Usado', value: 'used' },
        { text: 'Expirado', value: 'expired' }
      ]
    }
  },
  computed: {
    ...mapState('parking_reservations', ['reservations', 'codes']),
    ...mapGetters(['getProfile']),

    beneficiaryReservations() {
      return this.reservations.filter(r => r.role === 'beneficiary')
    },

    filteredCodes() {
      let filtered = this.codes

      if (this.filterCodeStatus) {
        filtered = filtered.filter(c => c.qr_status === this.filterCodeStatus)
      }

      if (this.filterCodeReservation) {
        filtered = filtered.filter(c => c.reservation_id === this.filterCodeReservation)
      }

      if (this.searchCodeText) {
        const search = this.searchCodeText.toLowerCase()
        filtered = filtered.filter(c =>
          c.provider_name.toLowerCase().includes(search)
        )
      }

      return filtered
    }
  },
  created() {
    this.fetchData()
  },
  watch: {
    selectedReservation(newVal) {
      // Recargar códigos cuando se selecciona una reserva
      if (newVal) {
        this.loadCodes()
      }
    }
  },
  methods: {
    ...mapActions('parking_reservations', [
      'FETCH_RESERVATIONS',
      'FETCH_CODES',
      'CREATE_CODE',
      'UPDATE_CODE_STATUS',
      'RESEND_CODE',
      'DELETE_CODE'
    ]),

    async fetchData() {
      this.loading = true
      this.error = null

      try {
        await this.FETCH_RESERVATIONS({ role: 'beneficiary' })
        // Cargar códigos en paralelo: tabla completa + conteos por reserva
        await Promise.all([
          this.loadCodes(),
          this.loadCodesCountForAllReservations()
        ])
      } catch (err) {
        this.error = 'Error al cargar datos'
        console.error('Error al cargar datos:', err)
      } finally {
        this.loading = false
      }
    },

    async loadCodesCountForAllReservations() {
      for (const reservation of this.beneficiaryReservations) {
        try {
          const response = await axios({
            method: 'get',
            url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/list/',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'Token ' + this.$store.getters.getToken
            },
            params: { reservation_id: reservation.id }
          })
          const count = Array.isArray(response.data) ? response.data.length : 0
          // this.$set garantiza reactividad en Vue 2 al agregar propiedades dinámicas
          this.$set(this.codesCountByReservation, reservation.id, count)
        } catch (error) {
          this.$set(this.codesCountByReservation, reservation.id, 0)
        }
      }
    },

    async loadCodes() {
      this.loadingCodes = true
      try {
        // Cargar todos los códigos sin restricción de reserva
        await this.FETCH_CODES({})
      } catch (err) {
        this.error = 'Error al cargar códigos'
        console.error('Error al cargar códigos:', err)
      } finally {
        this.loadingCodes = false
      }
    },

    selectReservation(reservation) {
      this.selectedReservation = reservation
    },

    openCreateCodeDialog() {
      if (!this.selectedReservation) return
      this.dialogCreateCode = true
    },

    openCreateCodeFromReservation(reservation) {
      this.selectedReservation = reservation
      this.dialogCreateCode = true
    },

    viewReservationDetails(reservation) {
      this.selectedReservation = reservation
    },

    async handleCodeCreated(result) {
      this.dialogCreateCode = false
      await Promise.all([
        this.loadCodes(),
        this.loadCodesCountForAllReservations()
      ])
      await this.FETCH_RESERVATIONS({ role: 'beneficiary' })
      this.$store.commit('snackbar_activate_true')
      this.$store.commit('snackbar_change_message', result && result.count > 1 ? `${result.count} códigos QR creados exitosamente` : 'Código QR creado exitosamente')
    },



    async viewCodeDetails(code) {
      this.selectedCode = code
      this.dialogCodeDetails = true
      
      // Hacer fetch para obtener detalles completos del código
      try {
        const config = {
          method: 'get',
          url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/' + code.id + '/',
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + this.$store.getters.getToken
          }
        }
        const response = await axios(config)
        // Actualizar selectedCode con detalles completos
        this.selectedCode = response.data
      } catch (error) {
        console.error('Error al obtener detalles del código:', error)
        // Si falla, mantener los datos que ya tenemos
      }
    },

    async toggleCodeStatus(code, action) {
      this.loading = true

      try {
        await this.UPDATE_CODE_STATUS({ id: code.id, action })
        await this.loadCodes()
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', `Código ${action === 'activate' ? 'activado' : 'desactivado'}`)
      } catch (err) {
        this.error = 'Error al actualizar código'
      } finally {
        this.loading = false
      }
    },

    openResendDialog(code) {
      this.selectedCode = code
      this.resendData.email = code.contact_email || ''
      this.resendData.phone = code.contact_phone || ''
      this.dialogResend = true
    },

    async handleResendCode() {
      if (!this.resendData.email && !this.resendData.phone) {
        this.error = 'Debes proporcionar al menos email o teléfono'
        return
      }

      this.loadingResend = true

      try {
        await this.RESEND_CODE({
          id: this.selectedCode.id,
          email: this.resendData.email,
          phone: this.resendData.phone
        })
        this.dialogResend = false
        await this.loadCodes()
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Código reenviado exitosamente')
      } catch (err) {
        this.error = 'Error al reenviar código'
      } finally {
        this.loadingResend = false
      }
    },

    openDeleteConfirmDialog(code) {
      this.codeToDelete = code
      this.deleteData.reason = ''
      this.deleteData.notify_provider = true
      this.dialogConfirmDelete = true
    },

    async handleDeleteCode() {
      if (!this.codeToDelete) return
      
      this.loading = true

      try {
        await this.DELETE_CODE({
          id: this.codeToDelete.id,
          reason: this.deleteData.reason,
          notify_provider: this.deleteData.notify_provider
        })
        
        this.dialogConfirmDelete = false
        this.codeToDelete = null
        
        // Recargar datos después de eliminar
        await Promise.all([
          this.loadCodes(),
          this.loadCodesCountForAllReservations()
        ])
        
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Código QR cancelado exitosamente. El visitante ha sido notificado.')
        this.$store.commit('snackbar_change_color', 'success')
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.detail) || err.message || 'Error al cancelar código'
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', this.error)
        this.$store.commit('snackbar_change_color', 'error')
      } finally {
        this.loading = false
      }
    },

    getCodesCountByReservation(reservationId) {
      return this.codesCountByReservation[reservationId] || 0
    },

    getExpirationColor(status) {
      const map = {
        'active': 'green',
        'expiring_soon': 'orange',
        'expires_today': 'deep-orange',
        'expired': 'red'
      }
      return map[status] || 'grey'
    },

    getExpirationIcon(status) {
      const map = {
        'active': 'check_circle',
        'expiring_soon': 'warning',
        'expires_today': 'schedule',
        'expired': 'error'
      }
      return map[status] || 'info'
    },

    formatExpirationMessage(code) {
      // Siempre calcular desde la fecha de expiración para tener un valor confiable
      if (code.expiration) {
        const today = moment().startOf('day')
        const expiration = moment(code.expiration).startOf('day')
        const daysRemaining = expiration.diff(today, 'days')
        
        if (daysRemaining < 0) {
          return 'Expirado'
        }
        if (daysRemaining === 0) {
          return 'Hoy'
        }
        return `${daysRemaining}d`
      }
      
      // Fallback: si no tenemos expiration, usar days_remaining
      if (code.days_remaining !== undefined && code.days_remaining !== null) {
        if (code.days_remaining < 0) {
          return 'Expirado'
        }
        if (code.days_remaining === 0) {
          return 'Hoy'
        }
        return `${code.days_remaining}d`
      }
      
      // Si nada de lo anterior, verificar qr_status
      if (code.qr_status === 'expired') {
        return 'Expirado'
      }
      
      return 'N/A'
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY')
    },

    formatDateTime(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY HH:mm')
    },

    formatStatus(status) {
      const map = {
        active: 'Activa',
        expired: 'Expirada',
        canceled: 'Cancelada'
      }
      return map[status] || status
    },

    formatQRStatus(status) {
      const map = {
        pending: 'Pendiente',
        active: 'Activo',
        inactive: 'Inactivo',
        in_use: 'En Uso',
        used: 'Usado',
        expired: 'Expirado'
      }
      return map[status] || status
    },

    getStatusColor(status) {
      const map = {
        active: 'green',
        expired: 'orange',
        canceled: 'red'
      }
      return map[status] || 'grey'
    },

    getQRStatusColor(status) {
      const map = {
        pending: 'grey',
        active: 'green',
        inactive: 'orange',
        in_use: 'blue',
        used: 'green',
        expired: 'red'
      }
      return map[status] || 'grey'
    },

    goToProvidersManagement() {
      this.$router.push({ name: 'transportistas' })
    },

    async downloadPDF(code) {
      try {
        // Obtener detalles completos del código si no los tenemos
        var codeData = code
        try {
          const config = {
            method: 'get',
            url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/codes/' + code.id + '/',
            headers: {
              'Content-Type': 'application/json',
              'authorization': 'Token ' + this.$store.getters.getToken
            }
          }
          const response = await axios(config)
          codeData = response.data
        } catch (e) {
          console.warn('No se pudieron obtener detalles completos, usando datos disponibles')
        }

        // Generar imagen del QR
        var qrValue = codeData.qr_code || code.qr_code || ''
        var qrDataUrl = ''
        if (qrValue) {
          qrDataUrl = await QRCode.toDataURL(qrValue, { width: 300, margin: 2 })
        }

        // Crear PDF
        var doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
        var pageW = doc.internal.pageSize.getWidth()
        var margin = 20
        var y = 20

        // Encabezado
        doc.setFillColor(25, 118, 210)
        doc.rect(0, 0, pageW, 30, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(16)
        doc.setFontStyle('bold')
        doc.text('Código de Acceso QR - Parking', margin, 19)

        y = 45
        doc.setTextColor(0, 0, 0)

        // QR image centrado
        if (qrDataUrl) {
          var qrSize = 60
          var qrX = (pageW - qrSize) / 2
          doc.addImage(qrDataUrl, 'PNG', qrX, y, qrSize, qrSize)
          y += qrSize + 8
        }

        // Código texto
        doc.setFontSize(10)
        doc.setFontStyle('normal')
        doc.setTextColor(100, 100, 100)
        doc.text('Código: ' + (codeData.qr_code || '-'), pageW / 2, y, { align: 'center' })
        y += 12

        // Divider
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, pageW - margin, y)
        y += 8

        // Datos del visitante
        doc.setFontSize(12)
        doc.setFontStyle('bold')
        doc.setTextColor(25, 118, 210)
        doc.text('Datos del Visitante', margin, y)
        y += 8

        doc.setFontSize(10)
        doc.setFontStyle('normal')
        doc.setTextColor(0, 0, 0)

        var fields = [
          ['Visitante', codeData.provider_name || (codeData.first_name + ' ' + (codeData.last_name || '')) || '-'],
          ['Empresa', codeData.company_name || codeData.beneficiary_company_name || '-'],
          ['Email', codeData.contact_email || codeData.email || '-'],
          ['Teléfono', codeData.contact_phone || codeData.phone || codeData.telephone || '-']
        ]

        fields.forEach(function(field) {
          doc.setFontStyle('bold')
          doc.text(field[0] + ':', margin, y)
          doc.setFontStyle('normal')
          doc.text(String(field[1]), margin + 35, y)
          y += 7
        })

        y += 4
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, pageW - margin, y)
        y += 8

        // Datos del acceso
        doc.setFontSize(12)
        doc.setFontStyle('bold')
        doc.setTextColor(25, 118, 210)
        doc.text('Datos del Acceso', margin, y)
        y += 8

        doc.setFontSize(10)
        doc.setFontStyle('normal')
        doc.setTextColor(0, 0, 0)

        var accessFields = [
          ['Cajón', codeData.number || codeData.space_number || '-'],
          ['Estado', this.formatQRStatus(codeData.qr_status || '')],
          ['Vigencia inicio', codeData.invitation_date ? this.formatDate(codeData.invitation_date) : '-'],
          ['Vigencia fin', codeData.expiration ? this.formatDate(codeData.expiration) : '-']
        ]

        // Agregar horas si existen
        if (codeData.start_time || codeData.end_time) {
          accessFields.push(['Hora inicio', codeData.start_time || '00:00'])
          accessFields.push(['Hora fin', codeData.end_time || '23:59'])
        }

        var self = this
        accessFields.forEach(function(field) {
          doc.setFontStyle('bold')
          doc.text(field[0] + ':', margin, y)
          doc.setFontStyle('normal')
          doc.text(String(field[1]), margin + 35, y)
          y += 7
        })

        // Footer
        y = doc.internal.pageSize.getHeight() - 15
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text('Generado el ' + moment().format('DD/MM/YYYY HH:mm'), margin, y)
        doc.text('Intrare - Sistema de Accesos', pageW - margin, y, { align: 'right' })

        // Descargar
        var filename = 'acceso_qr_' + (codeData.provider_name || 'visitante').replace(/\s+/g, '_') + '_' + moment().format('YYYYMMDD') + '.pdf'
        doc.save(filename)

      } catch (err) {
        console.error('Error generando PDF:', err)
        this.error = 'Error al generar el PDF'
      }
    }
  }
}
</script>

<style scoped>
.reservation-info {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
}

.info-row + .info-row {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.info-icon {
  flex-shrink: 0;
  width: 24px;
  margin-right: 12px;
  margin-top: 2px;
}

.info-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.info-label {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.54);
}

.info-value {
  font-size: 14px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.87);
}

.info-chip {
  align-self: flex-start;
  margin-top: 2px;
}
</style>
